"use client";

import { c as _c } from "react/compiler-runtime";
import type { useVirtualizedWaveDrops } from "@/hooks/useVirtualizedWaveDrops";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
type WaveMessagesResult = ReturnType<typeof useVirtualizedWaveDrops>["waveMessages"];
interface DeferredNewestDropsState {
  readonly renderedWaveMessages: WaveMessagesResult;
  readonly pendingDropsCount: number;
  readonly revealPendingDrops: () => void;
}
interface UseDeferredNewestDropsProps {
  readonly waveId: string;
  readonly isAppleMobile: boolean;
  readonly waveMessages: WaveMessagesResult;
  readonly shouldPinToBottom: boolean;
}
interface DeferredNewestDropsSession {
  readonly waveId: string;
  readonly wasPinned: boolean;
  readonly capturedLatestSerial: number | null;
  readonly revealedLatestSerial: number | null;
}
interface DeferredNewestDropsInputsSnapshot {
  readonly waveId: string;
  readonly latestSerialNo: number | null;
  readonly shouldPinToBottom: boolean;
}
const createDeferredNewestDropsSession = (waveId: string): DeferredNewestDropsSession => ({
  waveId,
  wasPinned: true,
  capturedLatestSerial: null,
  revealedLatestSerial: null
});
const updateDeferredNewestDropsSession = ({
  session,
  nextSession
}: {
  readonly session: DeferredNewestDropsSession;
  readonly nextSession: DeferredNewestDropsSession;
}): DeferredNewestDropsSession => session.waveId === nextSession.waveId && session.wasPinned === nextSession.wasPinned && session.capturedLatestSerial === nextSession.capturedLatestSerial && session.revealedLatestSerial === nextSession.revealedLatestSerial ? session : nextSession;
const getPreviousPinnedLatestSerial = ({
  previousInputs,
  waveId
}: {
  readonly previousInputs: DeferredNewestDropsInputsSnapshot | null;
  readonly waveId: string;
}): number | null => previousInputs?.waveId === waveId && previousInputs.shouldPinToBottom ? previousInputs.latestSerialNo : null;
const getDeferredNewestDropsVisibleLatestSerial = ({
  session,
  waveId,
  isAppleMobile,
  shouldPinToBottom,
  latestSerialNo
}: {
  readonly session: DeferredNewestDropsSession;
  readonly waveId: string;
  readonly isAppleMobile: boolean;
  readonly shouldPinToBottom: boolean;
  readonly latestSerialNo: number | null;
}): number | null => {
  if (!isAppleMobile || shouldPinToBottom || session.waveId !== waveId) {
    return latestSerialNo;
  }
  return session.revealedLatestSerial ?? session.capturedLatestSerial ?? latestSerialNo;
};
const getDeferredNewestDropsNextSession = ({
  session,
  waveId,
  isAppleMobile,
  shouldPinToBottom,
  latestSerialNo,
  previousInputs
}: {
  readonly session: DeferredNewestDropsSession;
  readonly waveId: string;
  readonly isAppleMobile: boolean;
  readonly shouldPinToBottom: boolean;
  readonly latestSerialNo: number | null;
  readonly previousInputs: DeferredNewestDropsInputsSnapshot | null;
}): DeferredNewestDropsSession => {
  let nextSession = session.waveId === waveId ? session : createDeferredNewestDropsSession(waveId);
  if (!isAppleMobile) {
    nextSession = {
      ...nextSession,
      wasPinned: shouldPinToBottom,
      capturedLatestSerial: null,
      revealedLatestSerial: null
    };
    return updateDeferredNewestDropsSession({
      session,
      nextSession
    });
  }
  if (shouldPinToBottom) {
    nextSession = {
      ...nextSession,
      wasPinned: true,
      capturedLatestSerial: null,
      revealedLatestSerial: null
    };
    return updateDeferredNewestDropsSession({
      session,
      nextSession
    });
  }
  const previousPinnedLatestSerial = getPreviousPinnedLatestSerial({
    previousInputs,
    waveId
  });
  const capturedLatestSerial = nextSession.wasPinned ? previousPinnedLatestSerial ?? latestSerialNo : nextSession.capturedLatestSerial;
  nextSession = {
    ...nextSession,
    wasPinned: false,
    capturedLatestSerial
  };
  return updateDeferredNewestDropsSession({
    session,
    nextSession
  });
};
const filterDeferredDrops = ({
  isAppleMobile,
  visibleLatestSerial,
  waveMessages
}: {
  readonly isAppleMobile: boolean;
  readonly visibleLatestSerial: number | null;
  readonly waveMessages: WaveMessagesResult;
}): WaveMessagesResult => {
  if (!waveMessages) {
    return waveMessages;
  }
  if (!isAppleMobile || visibleLatestSerial === null) {
    return waveMessages;
  }
  const filteredDrops = waveMessages.drops.filter(drop => {
    if (typeof drop.serial_no !== "number") {
      return true;
    }
    return drop.serial_no <= visibleLatestSerial;
  });
  if (filteredDrops.length === waveMessages.drops.length) {
    return waveMessages;
  }
  return {
    ...waveMessages,
    drops: filteredDrops
  };
};
const getPendingDropsCount = ({
  isAppleMobile,
  visibleLatestSerial,
  waveMessages
}: {
  readonly isAppleMobile: boolean;
  readonly visibleLatestSerial: number | null;
  readonly waveMessages: WaveMessagesResult;
}): number => {
  const drops = waveMessages?.drops;
  if (!isAppleMobile || !drops || drops.length === 0 || visibleLatestSerial === null) {
    return 0;
  }
  return drops.reduce((count, drop) => {
    if (typeof drop.serial_no !== "number") {
      return count;
    }
    return drop.serial_no > visibleLatestSerial ? count + 1 : count;
  }, 0);
};
export function useDeferredNewestDrops(t0) {
  const $ = _c(32);
  const {
    waveId,
    isAppleMobile,
    waveMessages,
    shouldPinToBottom
  } = t0;
  const latestSerialNo = waveMessages?.drops[0]?.serial_no ?? null;
  let t1;
  if ($[0] !== waveId) {
    t1 = () => createDeferredNewestDropsSession(waveId);
    $[0] = waveId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [session, setSession] = useState(t1);
  const sessionRef = useRef(session);
  const previousInputsRef = useRef(null);
  let t2;
  if ($[2] !== isAppleMobile || $[3] !== latestSerialNo || $[4] !== session || $[5] !== shouldPinToBottom || $[6] !== waveId) {
    t2 = getDeferredNewestDropsVisibleLatestSerial({
      session,
      waveId,
      isAppleMobile,
      shouldPinToBottom,
      latestSerialNo
    });
    $[2] = isAppleMobile;
    $[3] = latestSerialNo;
    $[4] = session;
    $[5] = shouldPinToBottom;
    $[6] = waveId;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const visibleLatestSerial = t2;
  let t3;
  let t4;
  if ($[8] !== session) {
    t3 = () => {
      sessionRef.current = session;
    };
    t4 = [session];
    $[8] = session;
    $[9] = t3;
    $[10] = t4;
  } else {
    t3 = $[9];
    t4 = $[10];
  }
  useLayoutEffect(t3, t4);
  let t5;
  let t6;
  if ($[11] !== isAppleMobile || $[12] !== latestSerialNo || $[13] !== session || $[14] !== shouldPinToBottom || $[15] !== waveId) {
    t5 = () => {
      const nextSession = getDeferredNewestDropsNextSession({
        session,
        waveId,
        isAppleMobile,
        shouldPinToBottom,
        latestSerialNo,
        previousInputs: previousInputsRef.current
      });
      previousInputsRef.current = {
        waveId,
        latestSerialNo,
        shouldPinToBottom
      };
      if (session !== nextSession) {
        setSession(nextSession);
      }
    };
    t6 = [isAppleMobile, latestSerialNo, session, shouldPinToBottom, waveId];
    $[11] = isAppleMobile;
    $[12] = latestSerialNo;
    $[13] = session;
    $[14] = shouldPinToBottom;
    $[15] = waveId;
    $[16] = t5;
    $[17] = t6;
  } else {
    t5 = $[16];
    t6 = $[17];
  }
  useLayoutEffect(t5, t6);
  let t7;
  if ($[18] !== isAppleMobile || $[19] !== visibleLatestSerial || $[20] !== waveMessages) {
    t7 = filterDeferredDrops({
      isAppleMobile,
      visibleLatestSerial,
      waveMessages
    });
    $[18] = isAppleMobile;
    $[19] = visibleLatestSerial;
    $[20] = waveMessages;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const renderedWaveMessages = t7;
  let t8;
  if ($[22] !== isAppleMobile || $[23] !== visibleLatestSerial || $[24] !== waveMessages) {
    t8 = getPendingDropsCount({
      isAppleMobile,
      visibleLatestSerial,
      waveMessages
    });
    $[22] = isAppleMobile;
    $[23] = visibleLatestSerial;
    $[24] = waveMessages;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const pendingDropsCount = t8;
  let t9;
  if ($[26] !== waveMessages?.drops) {
    t9 = () => {
      const newestSerial = waveMessages?.drops[0]?.serial_no ?? null;
      if (newestSerial === null || sessionRef.current.revealedLatestSerial === newestSerial) {
        return;
      }
      setSession(currentSession => currentSession.revealedLatestSerial === newestSerial ? currentSession : {
        ...currentSession,
        revealedLatestSerial: newestSerial
      });
    };
    $[26] = waveMessages?.drops;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  const revealPendingDrops = t9;
  let t10;
  if ($[28] !== pendingDropsCount || $[29] !== renderedWaveMessages || $[30] !== revealPendingDrops) {
    t10 = {
      renderedWaveMessages,
      pendingDropsCount,
      revealPendingDrops
    };
    $[28] = pendingDropsCount;
    $[29] = renderedWaveMessages;
    $[30] = revealPendingDrops;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  return t10;
}